import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useData from "../../../hooks/useData";
import {
  Form,
  Divider,
  Select,
  Col,
  Row,
  Tag,
  Flex,
  Input,
  InputNumber,
  Tabs,
  Upload,
  message,
  Collapse,
  Button,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import uniqid from "uniqid";
import Api from "../../../Api";
import { generateSKU } from "../../Cutting/utils";
import { BiSolidArrowToLeft } from "react-icons/bi";

const folderId = uniqid();
const AddOrEdit = () => {
  const { id } = useParams();
  const { data: destinations } = useData("/web/data/destinations", []);
  const { data: categories } = useData("/web/data/categories", []);
  const { data: collections } = useData("/web/data/collections", []);
  const { data: colors } = useData("/web/data/colors", []);
  const { data: attributes } = useData("/web/data/attributes", []);
  const { update } = useData(`/web/data/product`, {});
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState("");
  const [documentationFiles, setDocumentationFiles] = useState([]);
  const [submittable, setSubmittable] = React.useState(false);

  const [folder] = useState(folderId);
  const [form] = Form.useForm();

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);
  const handleSelectDestination = (value) => {
    setSelectedDestination(value);
    form.setFieldValue("category", undefined);
  };

  const getName = (arr, id, lang) => {
    const elem = arr?.find((item) => item._id === id);
    return id ? elem[`name_${lang}`] || elem[lang] : "";
  };
  const onFieldsChange = (v) => {
    if (!v[0].name[0].includes("short") && v.length === 1) {
      const languages = ["ru", "en", "ka"];
      languages.forEach((language) => {
        const width = form.getFieldValue("width");
        const height = form.getFieldValue("height");
        const depth = form.getFieldValue("depth");
        const destinationName = getName(
          destinations,
          form.getFieldValue("destination"),
          language,
        );
        const collectionName = getName(
          collections,
          form.getFieldValue("collection"),
          language,
        ).toLowerCase();
        const categoryName = getName(
          categories,
          form.getFieldValue("category"),
          language,
        );

        const colorNames =
          form
            .getFieldValue("colors")
            ?.map((color) => getName(colors, color, language)) || [];

        form.setFieldsValue({
          [`name_${language}`]: `${destinationName} ${collectionName} ${width && height ? `${width}x${height}` : width && !height ? `${width}` : ""} ${
            form.getFieldValue("colors")?.length
              ? `(${colorNames.join("/")})`
              : ""
          }`,
        });
        form.setFieldValue(
          `short_${language}`,
          `${collectionName} ${width && height ? `${width}x${height}` : width && !height ? `${width}` : ""}`,
        );
        form.setFieldValue(
          `path_${language}`,
          `${collectionName}_${width && height ? `${width}x${height}` : width && !height ? `${width}` : ""}`.replace(
            / /g,
            "_",
          ),
        );

        const additionWords = {
          ka: [
            "ავეჯი",
            "საწარმო",
            "ბათუმი",
            "პრემიუმ",
            "სტანდარტი",
            "შეძენა",
            "შეკვეთა",
          ],
          ru: [
            "мебель",
            "производства",
            "батуми",
            "премиум",
            "стандарт",
            "заказ",
            "покупка",
          ],
          en: [
            "furniture",
            "factory",
            "batumi",
            "premium",
            "standard",
            "order",
            "buy",
          ],
        };
        const dimensions =
          width && height && depth
            ? `${width}x${height}x${depth}`
            : width && height
              ? `${width}x${height}`
              : width
                ? width
                : "";
        form.setFieldValue(
          `keywords_${language}`,
          [
            destinationName,
            collectionName,
            ...colorNames,
            ...additionWords[language],
            ...(categoryName ? [categoryName] : []),
            ...(dimensions ? [dimensions] : []),
          ].filter(Boolean),
        );
        const description = {
          ka: `${destinationName} ${collectionName} ${categoryName}\n${width ? `სიგანე: ${width}` : ""} ${height ? `სიმაღლე: ${height}` : ""} ${depth ? `სიღრმე: ${depth}` : ""}\nხელმისაწვდომი ფერი(ები): ${colorNames.join(", ")}`,
          ru: `${destinationName} ${collectionName} ${categoryName}\n${width ? `Ширина: ${width}` : ""} ${height ? `Высота: ${height}` : ""} ${depth ? `Глубина: ${depth}` : ""}\nДоступные цвета: ${colorNames.join(", ")}`,
          en: `${destinationName} ${collectionName} ${categoryName}\n${width ? `Width: ${width}` : ""} ${height ? `Height: ${height}` : ""} ${depth ? `Depth: ${depth}` : ""}\nAvailable colors: ${colorNames.join(", ")}`,
        };
        form.setFieldValue(
          `seo_description_${language}`,
          description[language],
        );
      });
    }
  };

  const renderTab = (lang) => {
    return {
      key: lang,
      label:
        lang === "ru" ? "Русский" : lang === "en" ? "Английский" : "Грузинский",
      children: (
        <div>
          <Form.Item
            name={`short_${lang}`}
            label={`Название (${lang})`}
            rules={[
              { required: true, message: `Название ${lang} поле обязательна` },
            ]}
          >
            <Input
              onChange={(e) =>
                form.setFieldValue(`short_${lang}`, e.target.value)
              }
            />
          </Form.Item>
          <Form.Item
            name={`name_${lang}`}
            label={`Название (${lang})`}
            rules={[
              { required: true, message: `Название ${lang} поле обязательна` },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={`keywords_${lang}`}
            label={`Ключевые слова (${lang})`}
          >
            <Select mode={"tags"} />
          </Form.Item>
          <Form.Item
            name={`seo_description_${lang}`}
            label={`SEO Описание (${lang})`}
          >
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item
            name={`description_${lang}`}
            label={`Описание (${lang})`}
            rules={[
              {
                required: true,
                message: "Описание обязательно для заполнения",
                min: 30,
              },
            ]}
          >
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item
            name={`path_${lang}`}
            label={`URL (${lang})`}
            rules={[
              { required: true, message: `Название ${lang} поле обязательна` },
            ]}
          >
            <Input readOnly />
          </Form.Item>
        </div>
      ),
    };
  };

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
      }}
      type="button"
    >
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </button>
  );

  const generate = () => {
    form.setFieldValue("SKU", generateSKU(folder));
  };

  const handleSubmit = async () => {
    const values = form.getFieldsValue();
    const result = await update(
      {
        _id: id,
        name: {
          ru: values.short_ru,
          en: values.short_en,
          ka: values.short_ka,
        },
        description: {
          ru: values.description_ru,
          en: values.description_en,
          ka: values.description_ka,
        },
        SKU: values.SKU,
        meta: {
          title: {
            ru: values.name_ru,
            en: values.name_en,
            ka: values.name_ka,
          },
          keywords: {
            ru: values.keywords_ru,
            en: values.keywords_en,
            ka: values.keywords_ka,
          },
          description: {
            ru: values.seo_description_ru,
            en: values.seo_description_en,
            ka: values.seo_description_ka,
          },
        },
        height: values.height,
        width: values.width,
        depth: values.depth,
        price: values.price,
        colors: values.colors,
        category: values.category,
        destination: values.destination,
        collection: values.collection,
        attributes: values.attributes,
        path: {
          ru: values.path_ru,
          en: values.path_en,
          ka: values.path_ka,
        },
        folder,
      },
      "/web/addEdit?table=product",
    );
    if (result.data) {
      message.success("Продукт успешно добавлен");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{id ? "Редактировать" : "Создать"} продукт</h4>
              <p>{id ? `ID: ${id}` : "Не забудьте про поля SEO"}</p>
            </div>
          </div>
        </div>
        <Divider />
        <Form
          form={form}
          layout={"vertical"}
          size={"large"}
          onFieldsChange={onFieldsChange}
          style={{
            background: "#FFF",
            padding: 24,
            border: "1px solid #ddd",
            borderRadius: 6,
          }}
          wrapperCol={{ lg: 24 }}
          labelCol={{ lg: 22 }}
          onSubmitCapture={handleSubmit}
        >
          <Row gutter={[24, 0]}>
            <Col lg={6} xs={24}>
              <Form.Item name="collection" label="Коллекция">
                <Select allowClear>
                  {collections?.map((collection) => (
                    <Select.Option key={collection._id} value={collection._id}>
                      {collection.name_ru}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={6} xs={24}>
              <Form.Item
                name="destination"
                label="Направление"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={handleSelectDestination}
                  allowClear
                  options={destinations?.map((destination) => ({
                    label: destination.name_ru,
                    value: destination._id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col lg={5} xs={24}>
              <Form.Item
                name="category"
                label="Категория"
                rules={[{ required: true }]}
              >
                <Select
                  allowClear
                  options={categories
                    ?.filter(
                      (category) =>
                        category?.destination === selectedDestination,
                    )
                    .map((category) => ({
                      label: category.name_ru,
                      value: category._id,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col lg={2} xs={12}>
              <Form.Item
                name="price"
                label="Цена"
                rules={[
                  { required: true, message: "Цена обязательна для ввода" },
                ]}
              >
                <InputNumber
                  placeholder="Цена"
                  prefix={"₾"}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col lg={5}>
              <Form.Item
                name="SKU"
                label="SKU Code"
                rules={[
                  { required: true, message: "Нажмите кнопку сгенерировать" },
                ]}
              >
                <Input
                  readOnly
                  addonAfter={
                    <Button
                      type="link"
                      onClick={generate}
                      size="small"
                      htmlType="button"
                      ghost
                    >
                      Сгенерировать
                    </Button>
                  }
                />
              </Form.Item>
            </Col>
            <Col lg={7} xs={24}>
              <Form.Item
                name="colors"
                label="Цвет"
                rules={[{ required: true }]}
              >
                <Select
                  allowClear
                  options={colors?.map((color) => ({
                    label: color.ru,
                    value: color._id,
                    color: color.color,
                  }))}
                  mode={"multiple"}
                  onChange={setSelectedColors}
                  tagRender={(props) => (
                    <Tag style={{ background: "rgb(247 255 235)" }}>
                      <Flex gap={6} align={"center"}>
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 8,
                            backgroundColor: colors.find(
                              (color) => props.value === color._id,
                            ).color,
                            border: "1px solid #eee",
                            boxShadow: "0px 0px 2px 1px #ccc",
                          }}
                        />
                        {props.label}
                      </Flex>
                    </Tag>
                  )}
                  optionRender={(color) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 8,
                          backgroundColor: color.data.color,
                          border: "1px solid #eee",
                        }}
                      />
                      <span>{color.label}</span>
                    </div>
                  )}
                />
              </Form.Item>
            </Col>
            <Col lg={7}>
              <Form.Item name="attributes" label="Атрибуты">
                <Select
                  mode="multiple"
                  options={attributes?.map((attr) => ({
                    label: attr["ru"],
                    value: attr._id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Form.Item
              label={"Ширина"}
              style={{ paddingRight: 12 }}
              name="width"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={"Высота"}
              style={{ paddingRight: 12 }}
              name="height"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={"Глубина"}
              style={{ paddingRight: 12 }}
              name="depth"
            >
              <Input />
            </Form.Item>
          </Row>
          <Collapse accordion size={"small"}>
            <Collapse.Panel
              key={1}
              header="Описание товара (автозаполнение)"
              style={{ width: "100%" }}
            >
              <Tabs
                items={[
                  { ...renderTab("ru") },
                  { ...renderTab("en") },
                  { ...renderTab("ka") },
                ]}
              />
            </Collapse.Panel>
          </Collapse>

          <Tabs
            items={selectedColors?.map((color) => ({
              key: color,
              label: (
                <Flex gap={8} align={"center"}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      backgroundColor: colors?.find(
                        (item) => item._id === color,
                      ).color,
                      border: "1px solid #eee",
                    }}
                  />
                  <span>{getName(colors, color, "ru")}</span>
                </Flex>
              ),
              children: (
                <Upload
                  action={`${process.env.REACT_APP_API_URL}upload/multiple?id=${folder}&color=${colors?.find((c) => c._id === color)["en"].toLowerCase()}`}
                  listType="picture-card"
                  multiple
                  name={`image`}
                  onRemove={async (file) => {
                    const response = await Api.delete("/upload", {
                      data: {
                        key: file.response.imageUrls[0]?.replace(
                          "https://prestastorage90158-staging.s3.eu-central-1.amazonaws.com/",
                          "",
                        ),
                      },
                    });
                    if (response.status === 200) {
                      message.success("Фото удалена");
                    } else {
                      message.error("что-то пошло не так");
                    }
                  }}
                >
                  {uploadButton}
                </Upload>
              ),
            }))}
          />
          <Divider dashed>Файл сборки</Divider>
          <Upload
            action={`${process.env.REACT_APP_API_URL}uploadFile?id=${folder}`}
            listType="picture"
            name={`file`}
            maxCount={1}
            fileList={documentationFiles}
            onRemove={async (file) => {
              const response = await Api.delete("/upload", {
                data: {
                  key: file.response.url?.replace(
                    "https://prestastorage90158-staging.s3.eu-central-1.amazonaws.com/",
                    "",
                  ),
                },
              });
              if (response.status === 200) {
                message.success("Файл удален");
                setDocumentationFiles([]);
              } else {
                message.error("что-то пошло не так");
              }
            }}
            beforeUpload={(file) => {
              if (file.type !== "application/pdf") {
                message.error("Только PDF файлы можно загружать");
                return false;
              }
              setDocumentationFiles([file]);
              return true;
            }}
            onPreview={(file) => {
              return file.type === "application/pdf";
            }}
            onChange={(info) => {
              if (info.file.type === "application/pdf") {
                setDocumentationFiles(info.fileList);
              }

              if (info.file.status === "done") {
                message.success(`${info.file.name} файл загружен`);
                setDocumentationFiles([
                  {
                    ...info.file,
                    ...info.file.response,
                  },
                ]);
              } else if (info.file.status === "error") {
                message.error(`${info.file.name} невозможно загрузить файл`);
              }
            }}
          >
            {documentationFiles.length < 1 && (
              <Button icon={<UploadOutlined />} size={"small"} block>
                Выбрать файл .pdf
              </Button>
            )}
          </Upload>
          <Flex justify={"flex-end"} gap={12} className={"mt-4"}>
            <Link to="/web/product/list">
              <Button type={"primary"} ghost icon={<BiSolidArrowToLeft />}>
                К списку продуктов
              </Button>
            </Link>
            <Button
              type={"primary"}
              htmlType={"submit"}
              disabled={!submittable}
            >
              Добавить
            </Button>
          </Flex>
        </Form>
      </div>
    </div>
  );
};

export default AddOrEdit;
